import type { ReactNode } from "react";
import { Link } from "react-router";
import { ChevronRight, Image as ImageIcon, type LucideIcon } from "lucide-react";
import type { ListingCard } from "../../../api/catalogue";
import { formatPesos } from "../../../lib/money";
import { Badge, Icon } from "../../ui";
import { ListingPhoto } from "../ProductCard";
import { unitLabel } from "../partLabels";

/**
 * The part an admin put on the banner, at its normal price (desktop kit Hero). No offer, no
 * discount, no delivery promise: promotions aren't in v1. With none chosen, it invites the
 * customer to find their part by its diagram instead.
 */
export function HomeBanner({ listing }: { listing: ListingCard | null }) {
  return (
    <div className="relative isolate flex min-h-[340px] overflow-hidden rounded-xl bg-green-900">
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-(image:--gradient-sprout) opacity-35" />
      {listing ? <ChosenPart listing={listing} /> : <FindByDiagram />}
    </div>
  );
}

function ChosenPart({ listing }: { listing: ListingCard }) {
  return (
    <div className="flex w-full flex-col sm:flex-row">
      <div className="flex flex-1 flex-col justify-center gap-4 p-6 sm:p-10">
        <Badge tone="genuine" className="self-start">
          On the shelf now
        </Badge>
        <h2 className="m-0 type-display [font-size:var(--text-3xl)] tracking-tight text-white sm:[font-size:var(--text-4xl)]">{listing.name}</h2>
        <p className="m-0 type-body text-green-100">
          <span className="type-sku">{listing.reference_number ?? listing.sku}</span>
          {listing.dimension && ` · ${listing.dimension}`}
        </p>
        <p className="m-0 flex items-baseline gap-2">
          <span className="type-price [font-size:var(--text-2xl)] text-white">{formatPesos(listing.price_cents)}</span>
          <span className="type-small text-green-100">{unitLabel(listing.unit)} · tax included</span>
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-4">
          <BannerLink to={`/listings/${listing.id}`}>View this part</BannerLink>
          <Link to="/equipment-categories" className="inline-flex min-h-11 items-center gap-1 border-b-0 type-label text-white">
            Browse machines <Icon icon={ChevronRight} size={18} />
          </Link>
        </div>
      </div>
      <Link to={`/listings/${listing.id}`} tabIndex={-1} aria-hidden="true" className="flex border-b-0 bg-white sm:w-[42%] sm:flex-none">
        <ListingPhoto photo={listing.photo} alt="" className="aspect-[4/3] w-full sm:aspect-auto" />
      </Link>
    </div>
  );
}

function FindByDiagram() {
  return (
    <div className="flex max-w-[560px] flex-col justify-center gap-4 p-6 sm:p-10">
      <p className="m-0 type-eyebrow tracking-[var(--tracking-widest)] text-green-100 uppercase">Online parts catalogue</p>
      <h2 className="m-0 type-display [font-size:var(--text-3xl)] tracking-tight text-white sm:[font-size:var(--text-4xl)]">Find the right part by its diagram</h2>
      <p className="m-0 type-body [font-size:var(--text-md)] text-green-100">
        Choose your machine and open the assembly drawing. Every part is numbered on the diagram, so you order the right one first time.
      </p>
      <div className="mt-2">
        <BannerLink href="#pick-machine" icon={ImageIcon}>
          Pick your machine
        </BannerLink>
      </div>
    </div>
  );
}

/** The banner's main action: a link that looks like the large primary button. */
function BannerLink({ to, href, icon, children }: { to?: string; href?: string; icon?: LucideIcon; children: ReactNode }) {
  const className =
    "inline-flex h-(--control-lg) items-center gap-2 rounded-sm border border-green-700 bg-green-700 px-6 type-label [font-size:var(--text-md)] font-bold text-white hover:bg-green-800";
  const content = (
    <>
      {icon && <Icon icon={icon} size={20} />}
      <span>{children}</span>
    </>
  );
  return to ? (
    <Link to={to} className={className}>
      {content}
    </Link>
  ) : (
    <a href={href} className={className}>
      {content}
    </a>
  );
}
